/**
 * KioskCoffeeClubRedemption - Coffee Pass Status Banner for Kiosk
 * 
 * REDESIGNED: This is now just a STATUS BANNER, not a separate redemption flow.
 * 
 * How it works:
 * - Shows pass status (can redeem / already redeemed / outside window)
 * - Directs customers to browse the FULL Coffee & Drinks menu
 * - When they select a coffee item, the customization modal handles the redemption
 * - Pass holders see their first 8oz coffee as FREE with option to upgrade to 12oz
 */
import { Coffee, Clock, CheckCircle, Gift, ShoppingBag, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useKiosk } from './KioskContext';

export default function KioskCoffeeClubRedemption() {
  const { 
    coffeeClubStatus, 
    isAccessible,
    cart,
    setActiveCategory,
    setView
  } = useKiosk();
  
  // Check if cart already has a coffee pass redemption
  const hasRedemptionInCart = cart?.some(item => item.isCoffeeClubRedemption || item.isCoffeePassRedemption);

  // Don't show anything if no active pass
  if (!coffeeClubStatus?.has_active_pass) {
    return null;
  }

  const canRedeem = coffeeClubStatus?.can_redeem_now && !hasRedemptionInCart;
  const alreadyRedeemed = coffeeClubStatus?.already_redeemed_today || hasRedemptionInCart;
  const outsideWindow = !coffeeClubStatus?.redemption_window_open && !alreadyRedeemed;
  const hasAfternoonWindow = coffeeClubStatus?.has_afternoon_window;
  const planType = coffeeClubStatus?.plan_type;
  const redemptionWindows = coffeeClubStatus?.redemption_windows || '7:00 AM - 10:00 AM';

  // Navigate to coffee menu
  const goToCoffeeMenu = () => {
    setActiveCategory('coffee');
    setView('ordering-items');
  };

  return (
    <div className={`rounded-2xl overflow-hidden ${isAccessible ? 'mb-6' : 'mb-4'}`}>
      {/* CASE 1: Can redeem - direct them to the coffee menu */}
      {canRedeem && (
        <div className={`p-4 ${
          hasAfternoonWindow 
            ? 'bg-gradient-to-r from-purple-600 to-indigo-600'
            : 'bg-gradient-to-r from-green-600 to-emerald-600'
        }`}>
          <div className="flex items-center gap-3 mb-3">
            <div className={`rounded-full bg-white/20 flex items-center justify-center ${isAccessible ? 'w-14 h-14' : 'w-12 h-12'}`}>
              <Gift className={`text-white ${isAccessible ? 'w-7 h-7' : 'w-6 h-6'}`} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className={`font-bold text-white ${isAccessible ? 'text-2xl' : 'text-xl'}`}>
                  Your Free Coffee Awaits!
                </h3>
                {hasAfternoonWindow && (
                  <span className={`px-2 py-0.5 bg-white/20 rounded-full text-white ${isAccessible ? 'text-base' : 'text-xs'}`}>
                    {planType === 'extended' ? 'Extended Pass' : 'Upgraded Pass'}
                  </span>
                )}
              </div>
              <p className={`${hasAfternoonWindow ? 'text-purple-100' : 'text-green-100'} ${isAccessible ? 'text-lg' : 'text-sm'}`}>
                {coffeeClubStatus.member_name || 'Bula Pass Member'}
              </p>
            </div>
          </div>
          
          {/* Show redemption windows prominently for extended passes */}
          {hasAfternoonWindow && (
            <div className={`bg-white/15 rounded-xl p-2 mb-3 ${isAccessible ? 'text-base' : 'text-sm'}`}>
              <p className="text-white text-center">
                Your windows: <strong>{redemptionWindows}</strong>
              </p>
            </div>
          )}
          
          <div className={`bg-white/10 rounded-xl p-3 mb-3 ${isAccessible ? 'text-lg' : 'text-base'}`}>
            <p className="text-white">
              <strong>Browse the Coffee or Drinks menu</strong> and select your free 8oz drink. 
              Upgrade to 12oz for just $3!
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button
              onClick={goToCoffeeMenu}
              className={`flex-1 ${hasAfternoonWindow ? 'bg-white text-purple-700 hover:bg-purple-50' : 'bg-white text-green-700 hover:bg-green-50'} font-bold rounded-xl ${isAccessible ? 'py-5 text-xl' : 'py-4 text-lg'}`}
              data-testid="kiosk-go-to-coffee-btn"
            >
              <Coffee className={`mr-2 ${isAccessible ? 'w-6 h-6' : 'w-5 h-5'}`} />
              Go to Coffee Menu
            </Button>
            <Button
              onClick={() => { setActiveCategory('iced-drinks'); setView('ordering-items'); }}
              className={`flex-1 bg-white/20 text-white hover:bg-white/30 font-bold rounded-xl ${isAccessible ? 'py-5 text-xl' : 'py-4 text-lg'}`}
              data-testid="kiosk-go-to-drinks-btn"
            >
              Iced Drinks
            </Button>
          </div>
        </div>
      )}

      {/* CASE 2: Already redeemed today or in cart */}
      {alreadyRedeemed && (
        <div className="bg-gradient-to-r from-gray-700 to-gray-800 p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className={`rounded-full bg-green-500/30 flex items-center justify-center ${isAccessible ? 'w-12 h-12' : 'w-10 h-10'}`}>
              <CheckCircle className={`text-green-400 ${isAccessible ? 'w-6 h-6' : 'w-5 h-5'}`} />
            </div>
            <div>
              <h3 className={`font-bold text-white ${isAccessible ? 'text-xl' : 'text-lg'}`}>
                {hasRedemptionInCart ? 'Free Coffee in Cart!' : 'Already Redeemed Today'}
              </h3>
              <p className={`text-gray-400 ${isAccessible ? 'text-base' : 'text-sm'}`}>
                {hasRedemptionInCart 
                  ? 'Add more items or checkout when ready'
                  : 'Enjoy your free coffee! See you tomorrow ☕'
                }
              </p>
            </div>
          </div>
          
          {/* Quick navigation to add more items */}
          <div className="flex gap-2">
            <Button
              onClick={() => { setActiveCategory('pastries'); setView('ordering-items'); }}
              className={`flex-1 bg-orange-600 hover:bg-orange-700 text-white rounded-xl ${isAccessible ? 'py-4 text-lg' : 'py-3 text-base'}`}
              data-testid="kiosk-add-pastries-btn"
            >
              🥐 Add Pastries
            </Button>
            <Button
              onClick={() => { setActiveCategory('lunch'); setView('ordering-items'); }}
              className={`flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl ${isAccessible ? 'py-4 text-lg' : 'py-3 text-base'}`}
              data-testid="kiosk-add-lunch-btn"
            >
              🍽️ Add Lunch
            </Button>
            {hasRedemptionInCart && (
              <Button
                onClick={() => setView('cart')}
                className={`flex-1 bg-green-600 hover:bg-green-700 text-white rounded-xl ${isAccessible ? 'py-4 text-lg' : 'py-3 text-base'}`}
                data-testid="kiosk-view-cart-btn"
              >
                <ShoppingBag className="w-5 h-5 mr-1" />
                Checkout
              </Button>
            )}
          </div>
        </div>
      )}

      {/* CASE 3: Outside redemption window */}
      {outsideWindow && (
        <div className={`p-4 ${
          hasAfternoonWindow 
            ? 'bg-gradient-to-r from-purple-700 to-indigo-800'
            : 'bg-gradient-to-r from-blue-700 to-blue-800'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`rounded-full flex items-center justify-center ${
              hasAfternoonWindow ? 'bg-purple-500/30' : 'bg-blue-500/30'
            } ${isAccessible ? 'w-12 h-12' : 'w-10 h-10'}`}>
              <Clock className={`${hasAfternoonWindow ? 'text-purple-300' : 'text-blue-300'} ${isAccessible ? 'w-6 h-6' : 'w-5 h-5'}`} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className={`font-bold text-white ${isAccessible ? 'text-xl' : 'text-lg'}`}>
                  Bula Coffee Pass
                </h3>
                {hasAfternoonWindow && (
                  <span className={`px-2 py-0.5 bg-purple-400/30 rounded-full text-purple-200 ${isAccessible ? 'text-sm' : 'text-xs'}`}>
                    {planType === 'extended' ? 'Extended' : 'Upgraded'}
                  </span>
                )}
              </div>
              <p className={`${hasAfternoonWindow ? 'text-purple-200' : 'text-blue-200'} ${isAccessible ? 'text-base' : 'text-sm'}`}>
                {coffeeClubStatus.window_message || 'Redemption window closed'}
              </p>
              <div className={`mt-1 ${hasAfternoonWindow ? 'text-purple-300' : 'text-blue-300'} ${isAccessible ? 'text-sm' : 'text-xs'}`}>
                <p className="font-semibold">Your redemption hours:</p>
                <p className={`font-bold ${hasAfternoonWindow ? 'text-purple-100' : 'text-blue-100'}`}>
                  {redemptionWindows}
                </p>
                {hasAfternoonWindow && (
                  <p className="text-purple-200 mt-1">You have morning AND afternoon windows!</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
